<template>
  <div class="master-repair-infos">
    <div class="row">
      <div class="col-md-12">
        <KTCodePreview v-bind:title="'Danh mục hạng mục dịch vụ'">
          <template v-slot:toolbar>
            <div class="d-flex">
              <button
                type="button"
                v-if="canManage"
                class="btn btn-primary font-weight-bolder btn-sm"
                @click="openAddModal"
              >
                <i style="font-size: 1rem" class="flaticon2-add-1"></i>
                Tạo mới
              </button>
            </div>
          </template>

          <template v-slot:preview>
            <div class="col-md-12 mb-4">
              <b-form @submit.prevent="onSearch">
                <div class="row">
                  <b-input
                    size="sm"
                    placeholder="Tìm theo mã hoặc tên hạng mục..."
                    v-model.trim="search"
                    class="col-md-4"
                    @input="onInputChange"
                    v-on:keyup.enter="onSearch"
                  ></b-input>
                </div>
              </b-form>
            </div>

            <b-table
              :items="rows"
              :fields="fields"
              :busy="loading"
              bordered
              hover
              show-empty
              empty-text="Không có dữ liệu"
              class="table-vertical-center"
            >
              <template #table-busy>
                <div class="text-center text-primary my-2">
                  <b-spinner class="align-middle"></b-spinner>
                  <strong>Đang tải...</strong>
                </div>
              </template>

              <template #cell(stt)="data">
                {{ (page - 1) * limit + data.index + 1 }}
              </template>

              <template #cell(description)="data">
                <span class="text-muted">{{ data.item.description || '—' }}</span>
              </template>

              <template #cell(actions)="data">
                <div class="d-flex justify-content-center">
                  <button
                    v-if="canManage"
                    class="btn btn-icon btn-sm btn-outline-primary mr-2"
                    @click="openEditModal(data.item)"
                    title="Sửa"
                  >
                    <i class="flaticon-edit-1 p-0"></i>
                  </button>
                  <button
                    v-if="canManage"
                    class="btn btn-icon btn-sm btn-outline-danger"
                    @click="confirmDelete(data.item)"
                    title="Xoá"
                  >
                    <i class="flaticon2-rubbish-bin-delete-button p-0"></i>
                  </button>
                </div>
              </template>
            </b-table>

            <b-row class="mt-3">
              <b-col>
                <p class="text-dark" style="font-weight: 500">
                  Tổng số hạng mục: {{ totalRow }}
                </p>
              </b-col>
              <b-col>
                <b-pagination
                  v-show="totalPage >= 2"
                  v-model="page"
                  :total-rows="totalRow"
                  :per-page="limit"
                  align="right"
                  size="sm"
                  @change="onPageChange"
                ></b-pagination>
              </b-col>
            </b-row>
          </template>
        </KTCodePreview>
      </div>
    </div>

    <!-- Modal Add/Edit -->
    <b-modal
      id="modal-repair-info-form"
      :title="isEdit ? 'Sửa hạng mục dịch vụ' : 'Tạo hạng mục dịch vụ'"
      hide-footer
      no-close-on-backdrop
      @hidden="resetForm"
    >
      <b-form @submit.prevent="onSubmit">
        <b-form-group label-for="input-repair-code">
          <template v-slot:label>
            Mã hạng mục <span class="text-danger">*</span>
          </template>
          <b-form-input
            id="input-repair-code"
            v-model.trim="$v.form.repairCode.$model"
            :state="validateState('repairCode')"
            placeholder="VD: R001"
            size="sm"
          ></b-form-input>
          <b-form-invalid-feedback>
            Mã hạng mục là bắt buộc và tối đa 50 ký tự.
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label-for="input-repair-name">
          <template v-slot:label>
            Tên hạng mục <span class="text-danger">*</span>
          </template>
          <b-form-input
            id="input-repair-name"
            v-model.trim="$v.form.repairName.$model"
            :state="validateState('repairName')"
            placeholder="VD: Thay màn hình"
            size="sm"
          ></b-form-input>
          <b-form-invalid-feedback>
            Tên hạng mục là bắt buộc và tối đa 255 ký tự.
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label-for="input-description" label="Mô tả">
          <b-form-textarea
            id="input-description"
            v-model.trim="form.description"
            placeholder="Mô tả chi tiết (tuỳ chọn)..."
            rows="2"
            max-rows="4"
            size="sm"
          ></b-form-textarea>
        </b-form-group>

        <div class="d-flex justify-content-end mt-4">
          <b-button
            variant="secondary"
            size="sm"
            class="mr-2"
            @click="$bvModal.hide('modal-repair-info-form')"
          >
            Hủy
          </b-button>
          <b-button
            type="submit"
            variant="primary"
            size="sm"
            :disabled="submitting"
          >
            {{ submitting ? 'Đang lưu...' : 'Lưu' }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import ApiService from '@/core/services/api.service';
import { validationMixin } from 'vuelidate';
import { required, maxLength } from 'vuelidate/lib/validators';
import debounce from 'debounce';
import SavaDataUtil from '@/utils/saveDataToLocal';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import { makeToastSuccess, makeToastFaile } from '@/utils/common';
import { extractError } from '@/utils/warranty-service.helpers';

const API_BASE = 'v2/warranty-service/repair-infos';

export default {
  name: 'MasterRepairInfos',
  mixins: [validationMixin],
  components: { KTCodePreview },
  data() {
    return {
      rows: [],
      loading: false,
      search: '',
      page: 1,
      limit: 20,
      totalRow: 0,
      totalPage: 0,
      fields: [
        { key: 'stt', label: 'STT', tdClass: 'text-center', thStyle: { width: '6%' } },
        { key: 'repairCode', label: 'Mã hạng mục', thStyle: { width: '18%' } },
        { key: 'repairName', label: 'Tên hạng mục', thStyle: { width: '30%' } },
        { key: 'description', label: 'Mô tả' },
        {
          key: 'actions',
          label: 'Thao tác',
          tdClass: 'text-center',
          thStyle: { width: '12%' },
        },
      ],
      isEdit: false,
      submitting: false,
      form: {
        id: null,
        repairCode: '',
        repairName: '',
        description: '',
      },
    };
  },
  validations: {
    form: {
      repairCode: { required, maxLength: maxLength(50) },
      repairName: { required, maxLength: maxLength(255) },
    },
  },
  computed: {
    canManage() {
      return SavaDataUtil.checkPermission('WARRANTY_SERVICE_MANAGE_MASTER');
    },
  },
  created() {
    this.fetchData();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Dịch vụ bảo hành' },
      { title: 'Hạng mục dịch vụ' },
    ]);
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const { data } = await ApiService.query(API_BASE, {
          params: { search: this.search, page: this.page, limit: this.limit },
        });
        const payload = data.data || {};
        this.rows = payload.dataset || [];
        this.totalRow = payload.totalRow || 0;
        this.totalPage = payload.totalPage || 0;
      } catch (err) {
        makeToastFaile(extractError(err));
      } finally {
        this.loading = false;
      }
    },
    onSearch() {
      this.page = 1;
      this.fetchData();
    },
    onPageChange(newPage) {
      this.page = newPage;
      this.$nextTick(() => this.fetchData());
    },
    debouncedSearch: debounce(function () {
      this.onSearch();
    }, 400),
    onInputChange() {
      this.debouncedSearch();
    },

    openAddModal() {
      this.isEdit = false;
      this.resetForm();
      this.$bvModal.show('modal-repair-info-form');
    },
    openEditModal(item) {
      this.isEdit = true;
      this.form = {
        id: item.id,
        repairCode: item.repairCode,
        repairName: item.repairName,
        description: item.description || '',
      };
      this.$nextTick(() => {
        this.$v.form.$reset();
        this.$bvModal.show('modal-repair-info-form');
      });
    },
    resetForm() {
      this.form = { id: null, repairCode: '', repairName: '', description: '' };
      this.$v.form.$reset();
    },
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },
    async onSubmit() {
      this.$v.form.$touch();
      if (this.$v.form.$anyError) return;

      this.submitting = true;
      try {
        if (this.isEdit) {
          await ApiService.put(API_BASE, {
            id: this.form.id,
            repairCode: this.form.repairCode,
            repairName: this.form.repairName,
            description: this.form.description,
          });
          makeToastSuccess('Cập nhật hạng mục thành công');
        } else {
          await ApiService.post(API_BASE, {
            repairCode: this.form.repairCode,
            repairName: this.form.repairName,
            description: this.form.description,
          });
          makeToastSuccess('Tạo hạng mục thành công');
        }
        this.$bvModal.hide('modal-repair-info-form');
        this.fetchData();
      } catch (err) {
        makeToastFaile(extractError(err));
      } finally {
        this.submitting = false;
      }
    },

    async confirmDelete(item) {
      const ok = await this.$bvModal.msgBoxConfirm(
        `Xoá hạng mục "${item.repairCode} - ${item.repairName}"?`,
        {
          title: 'Xác nhận xoá',
          okTitle: 'Xoá',
          cancelTitle: 'Huỷ',
          okVariant: 'danger',
          centered: true,
        },
      );
      if (!ok) return;

      try {
        await ApiService.delete(`${API_BASE}/${item.id}`);
        makeToastSuccess('Đã xoá hạng mục');
        this.fetchData();
      } catch (err) {
        makeToastFaile(extractError(err));
      }
    },

  },
};
</script>

<style lang="scss" scoped>
.master-repair-infos {
  ::v-deep .table-vertical-center td {
    vertical-align: middle;
  }
}
</style>
